import { Box, Stack, Tab, Tabs } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { getCohorts } from 'api/cohorts'
import {
  getCareArticlesEngagementPerUserReport,
  getCertificatesPerUserReport,
  getCourseEngagementPerUserReport,
  getEngagementPerSessionReport,
  getLearnLibraryEngagementPerUserReport,
  getLessonEngagementPerUserReport,
  getQueryEngagementPerUserReport,
  getSearchesPerUserReport,
  getSharePerUserReport,
  getTotalEngagementPerUserReport,
} from 'api/reports'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import dateRenderer from 'components/column-renderers/date'
import { formatOrgStateUnit } from 'components/column-renderers/orgStateUnit'
import { useAuthContext } from 'components/context/AuthProvider'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getTitle } from 'components/tabs/TabsTitle'
import { createCohortsColumn, createCohortsCsvColumn } from 'features/Cohorts'
import { getActionColumn, RowAction, UIFilterAsyncAutocomplete } from 'features/UI'
import { CSVField, useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useRefreshTable } from 'hooks/useRefreshTable'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import {
  BaseTableRequest,
  CareArticlePortal,
  CareArticleType,
  CertificateRuleTypeLabel,
  EngagementPerSession,
  EngagementPerUserCertificate,
  EngagementPerUserSearches,
  EngagementReportTypes,
  EOp,
  FilterModel,
  FilterModelNumberArray,
  FilterModelText,
  Nullish,
  Option,
  ReportUser,
  SharedPerUserReport,
} from 'types'
import { formatDate, formatMetricDate, getRoute, useGetDataSource } from 'utils'
import { createEngagementColumn, createEngagementCsvColumn } from '../../helpers/engagementColumn'
import {
  createColumnExamAttempts,
  createCsvColumnExamAttempts,
} from '../../helpers/examAttemptsColumn'
import { createColumnPassedExam, createCsvColumnPassedExam } from '../../helpers/passedExamColumn'
import { prepareDateRangeParams } from '../../helpers/prepareDateRangeParams'
import { applyContentNameFilter, getSharingContentType } from '../../helpers/sharingColumns'
import { useDateRange } from '../../hooks/useDateRange'
import { useSharedContentTypeFilter } from '../../hooks/useSharedContentTypeFilter'
import { useUserTypeFilter } from '../../hooks/useUserTypeFilter'
import { DateRangeToolbar } from '../DateRangeToolbar/DateRangeToolbar'

const getAccessCodeValue = ({ data }: { data: ReportUser }) => {
  return data?.licenseExpiredAt || data?.isLicenseExpired ? 'unlicensed' : data?.userAccessCode
}

const activeColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  createCohortsColumn('userCohorts'),
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  { headerName: 'Course Name', field: 'courseName', filter: 'agTextColumnFilter' },
  createEngagementColumn('Course Active Engagement', 'activeEngagement'),
  createColumnExamAttempts('Exam', 'completedCourses'),
  createColumnPassedExam(),
]

const lessonColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  createCohortsColumn('userCohorts'),
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  { headerName: 'Course Name', field: 'courseName', filter: 'agTextColumnFilter' },
  { headerName: 'Lesson Name', field: 'lessonName', filter: 'agTextColumnFilter' },
  createEngagementColumn('Lesson active engagement', 'activeEngagement'),
  createColumnExamAttempts('Quiz', 'completedLessons'),
  createColumnPassedExam(),
]

const learnArticlesColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  createCohortsColumn('userCohorts'),
  { headerName: 'Learn Article Name', field: 'learnArticleName', filter: 'agTextColumnFilter' },
  { headerName: 'Learn Topic Name', field: 'learnTopicName', filter: 'agTextColumnFilter' },
  createEngagementColumn('Active Engagement', 'activeEngagement'),
]

const careArticlesColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  createCohortsColumn('userCohorts'),
  {
    headerName: 'Timeline Post Name',
    field: 'careArticleName',
    filter: 'agTextColumnFilter',
    minWidth: 220,
  },
  {
    headerName: 'Timeline Post Type',
    field: 'careArticleType',
    minWidth: 220,
    cellRenderer: (props: ICellRendererParams<{ careArticleType: CareArticleType }>) => {
      const careArticleType = props.data?.careArticleType
      return careArticleType === CareArticleType.Care ? 'Guide Post' : 'Action Post'
    },
  },
  createEngagementColumn('Active Engagement', 'activeEngagement'),
]

const queryColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  createCohortsColumn('userCohorts'),
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  { headerName: 'Query Name', field: 'queryName', filter: 'agTextColumnFilter', minWidth: 220 },
  {
    headerName: 'Linked Article',
    field: 'learnArticleName',
    filter: 'agTextColumnFilter',
    minWidth: 220,
  },
  {
    headerName: 'Portal',
    cellRenderer: (props: ICellRendererParams<{ queryPortal: CareArticlePortal }>) => {
      const portal = props?.data?.queryPortal
      return portal === CareArticlePortal.Caregiver ? 'Caregiver' : 'Trainee'
    },
    minWidth: 160,
    colId: 'queryPortal',
    field: 'queryPortal',
  },
  createEngagementColumn('Active Engagement', 'activeEngagement'),
]

const totalColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  createCohortsColumn('userCohorts'),
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  {
    headerName: 'Last Notification',
    field: 'lastNotificationSentAt',
    cellRenderer: dateRenderer('lastNotificationSentAt'),
  },
  {
    headerName: 'First Login',
    field: 'userFirstLoggedInAt',
    cellRenderer: columnRenderers.date('userFirstLoggedInAt', 'YYYY-MM-DD HH:mm:ss'),
  },
  {
    headerName: 'Last Login',
    field: 'userLastLoggedInAt',
    cellRenderer: columnRenderers.date('userLastLoggedInAt', 'YYYY-MM-DD HH:mm:ss'),
  },
  {
    headerName: 'Total Numbers of Logins',
    field: 'userTotalNumbersOfLogin',
    minWidth: 230,
  },
  {
    headerName: 'Passed Courses',
    field: 'passedCoursesCount',
  },
  {
    headerName: 'Finished Lessons',
    field: 'passedLessonsCount',
  },
  createEngagementColumn('Lessons Reading', 'lessonTime'),
  createEngagementColumn('Quizzes', 'quizTime'),
  createEngagementColumn('Exams', 'questionnaireTime'),
  createEngagementColumn('Learn Articles reading', 'learnArticleTime'),
  createEngagementColumn('Guide Posts reading', 'careArticleTime'),
  createEngagementColumn('Action Posts reading', 'taskArticleTime'),
  createEngagementColumn('Queries', 'queryTime'),
  {
    headerName: 'Shared Content',
    field: 'contentShareCount',
  },
  createEngagementColumn('Total Time online', 'totalEngagement'),
  createEngagementColumn('Lessons EN Audios', 'lessonEnAudioTime'),
  createEngagementColumn('Lessons ES Audios', 'lessonEsAudioTime'),
  createEngagementColumn('Quizzes EN Audios', 'quizEnAudioTime'),
  createEngagementColumn('Quizzes ES Audios', 'quizEsAudioTime'),
  createEngagementColumn('Exams EN Audios', 'questionnaireEnAudioTime'),
  createEngagementColumn('Exams ES Audios', 'questionnaireEsAudioTime'),
  createEngagementColumn('Learn Articles EN Audios', 'learnArticleEnAudioTime'),
  createEngagementColumn('Learn Articles ES Audios', 'learnArticleEsAudioTime'),
  createEngagementColumn('Guide Posts EN Audios', 'careArticleEnAudioTime'),
  createEngagementColumn('Guide Posts ES Audios', 'careArticleEsAudioTime'),
  createEngagementColumn('Action Posts EN Audios', 'taskArticleEnAudioTime'),
  createEngagementColumn('Action Posts ES Audios', 'taskArticleEsAudioTime'),
  createEngagementColumn('Queries EN Audios', 'queryEnAudioTime'),
  createEngagementColumn('Queries ES Audios', 'queryEsAudioTime'),
]

const sessionColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  createCohortsColumn('userCohorts'),
  {
    headerName: 'Login Date',
    field: 'startedAt',
    cellRenderer: columnRenderers.date('startedAt', 'YYYY-MM-DD HH:mm:ss'),
    minWidth: 200,
  },
  createEngagementColumn('Total Login Time', 'totalTime'),
]

const searchesColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'anonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'firstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'lastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'email', filter: 'agTextColumnFilter' },
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  createCohortsColumn('cohorts'),
  {
    headerName: 'Login Date',
    field: 'loginDate',
    cellRenderer: columnRenderers.date('loginDate', 'YYYY-MM-DD HH:mm:ss'),
    minWidth: 200,
  },
  { headerName: 'Terms or Phrase', field: 'text', filter: 'agTextColumnFilter' },
  { headerName: 'Type', field: 'type' },
  { headerName: 'Number of Searches', field: 'countOfSearches' },
  { headerName: 'Found Content', field: 'countOfResults' },
]

export const certificatesColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'anonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'firstname', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'lastname', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'email', filter: 'agTextColumnFilter' },
  {
    headerName: 'ORG/State/UNIT',
    field: 'organizationName',
    minWidth: 300,
    cellRenderer: columnRenderers.accessCodeRenderer,
  },
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  createCohortsColumn('cohorts'),
  { headerName: 'Certificate Name', field: 'name', filter: 'agTextColumnFilter', minWidth: 300 },
  {
    headerName: 'Date',
    field: 'createdAt',
    cellRenderer: columnRenderers.date('createdAt'),
  },
  {
    headerName: 'Certificate Type',
    field: 'type',
    valueGetter: ({ data }: { data: EngagementPerUserCertificate }) => {
      if (!data) return PLACEHOLDER
      return CertificateRuleTypeLabel[data.type] || PLACEHOLDER
    },
  },
  { headerName: 'Time Spend (h)', field: 'timeSpent' },
]

export const sharingColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter' },
  {
    headerName: 'Access Code',
    field: 'userAccessCode',
    filter: 'agTextColumnFilter',
    valueGetter: getAccessCodeValue,
  },
  createCohortsColumn('userCohorts'),
  {
    headerName: 'Shared Date',
    field: 'sharedAt',
    cellRenderer: columnRenderers.date('sharedAt'),
  },
  {
    headerName: 'Content Type',
    field: 'contentType',
    cellRenderer: (props: ICellRendererParams<SharedPerUserReport>): string => {
      if (!props.data) return PLACEHOLDER
      return getSharingContentType(props.data)
    },
  },
  {
    headerName: 'Title',
    field: 'contentName',
    filter: 'agTextColumnFilter',
    minWidth: 300,
  },
  {
    headerName: 'Shared With',
    field: 'receiverEmail',
    filter: 'agTextColumnFilter',
    minWidth: 500,
    cellRenderer: (props: ICellRendererParams<SharedPerUserReport>): string => {
      if (!props.data) return PLACEHOLDER

      const { receiverEmail, receiverFirstName, receiverLastName } = props?.data
      const fullName = `${receiverFirstName} ${receiverLastName}`

      return `${receiverEmail} / ${fullName}`
    },
  },
]

const csvFieldsActive: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last Name', value: 'userLastName' },
  { label: 'Login', value: 'userEmail' },
  createCohortsCsvColumn('userCohorts'),
  { label: 'Access Code', value: 'userAccessCode' },
  { label: 'Course Name', value: 'courseName' },
  createEngagementCsvColumn('Course Active Engagement', 'activeEngagement'),
  createCsvColumnExamAttempts('Exam attempts', 'completedCourses'),
  createCsvColumnPassedExam('completedCourses'),
]

const csvFieldsLesson: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last Name', value: 'userLastName' },
  { label: 'Login', value: 'userEmail' },
  createCohortsCsvColumn('userCohorts'),
  { label: 'Access Code', value: 'userAccessCode' },
  { label: 'Lesson Name', value: 'lessonName' },
  { label: 'Course Name', value: 'courseName' },
  createEngagementCsvColumn('Active Engagement', 'activeEngagement'),
  createCsvColumnExamAttempts('Quiz attempts', 'completedLessons'),
  createCsvColumnPassedExam('completedLessons'),
]

const csvFieldsLearnArticles: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  {
    label: 'First Name',
    value: 'userFirstName',
  },
  {
    label: 'Last Name',
    value: 'userLastName',
  },
  {
    label: 'Login',
    value: 'userEmail',
  },
  {
    label: 'Access Code',
    value: 'userAccessCode',
  },
  createCohortsCsvColumn('userCohorts'),
  {
    label: 'Learn Article Name',
    value: 'learnArticleName',
  },
  {
    label: 'Learn Topic Name',
    value: 'learnTopicName',
  },
  createEngagementCsvColumn('Active Engagement', 'activeEngagement'),
]

const csvFieldsCareArticles: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last Name', value: 'userLastName' },
  { label: 'Login', value: 'userEmail' },
  { label: 'Access Code', value: 'userAccessCode' },
  createCohortsCsvColumn('userCohorts'),
  { label: 'Timeline Post Name', value: 'careArticleName' },
  {
    label: 'Timeline Post Type',
    value: (data: Partial<{ careArticleType: CareArticleType }>) => {
      return data?.careArticleType === CareArticleType.Care ? 'Guide Post' : 'Action Post'
    },
  },
  createEngagementCsvColumn('Active Engagement', 'activeEngagement'),
]

const csvFieldsQuery: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last Name', value: 'userLastName' },
  { label: 'Login', value: 'userEmail' },
  createCohortsCsvColumn('userCohorts'),
  { label: 'Access Code', value: 'userAccessCode' },
  { label: 'Query Name', value: 'queryName' },
  { label: 'Linked Article', value: 'learnArticleName' },
  {
    label: 'Portal',
    value: ({ queryPortal }) =>
      queryPortal === CareArticlePortal.Caregiver ? 'Caregiver' : 'Trainee',
  },
  createEngagementCsvColumn('Active Engagement', 'activeEngagement'),
]

const csvFieldsTotal: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last name', value: 'userLastName' },
  { label: 'Login', value: 'userEmail' },
  createCohortsCsvColumn('userCohorts'),
  { label: 'AccessCode', value: 'userAccessCode' },
  {
    label: 'Last Notification',
    value: ({ lastNotificationSentAt }) => {
      if (!lastNotificationSentAt) return PLACEHOLDER

      return formatDate(lastNotificationSentAt)
    },
  },
  {
    label: 'First Login',
    value: ({ userFirstLoggedInAt }) => formatMetricDate(userFirstLoggedInAt),
  },
  {
    label: 'Last Login',
    value: ({ userLastLoggedInAt }) => formatMetricDate(userLastLoggedInAt),
  },
  { label: 'Total Numbers of Logins', value: 'userTotalNumbersOfLogin' },
  { label: 'Passed Courses', value: 'passedCoursesCount' },
  { label: 'Finished Lessons', value: 'passedLessonsCount' },
  createEngagementCsvColumn('Lessons Reading', 'lessonTime'),
  createEngagementCsvColumn('Quizzes', 'quizTime'),
  createEngagementCsvColumn('Exams', 'questionnaireTime'),
  createEngagementCsvColumn('Learn Articles reading', 'learnArticleTime'),
  createEngagementCsvColumn('Guide Posts reading', 'careArticleTime'),
  createEngagementCsvColumn('Action Posts reading', 'taskArticleTime'),
  createEngagementCsvColumn('Queries', 'queryTime'),
  { label: 'Shared Content', value: 'contentShareCount' },
  createEngagementCsvColumn('Total Engagement online', 'totalEngagement'),
  createEngagementCsvColumn('Lessons EN Audios', 'lessonEnAudioTime'),
  createEngagementCsvColumn('Lessons ES Audios', 'lessonEsAudioTime'),
  createEngagementCsvColumn('Quizzes EN Audios', 'quizEnAudioTime'),
  createEngagementCsvColumn('Quizzes ES Audios', 'quizEsAudioTime'),
  createEngagementCsvColumn('Exams EN Audios', 'questionnaireEnAudioTime'),
  createEngagementCsvColumn('Exams ES Audios', 'questionnaireEsAudioTime'),
  createEngagementCsvColumn('Learn Articles EN Audios', 'learnArticleEnAudioTime'),
  createEngagementCsvColumn('Learn Articles ES Audios', 'learnArticleEsAudioTime'),
  createEngagementCsvColumn('Guide Posts EN Audios', 'careArticleEnAudioTime'),
  createEngagementCsvColumn('Guide Posts ES Audios', 'careArticleEsAudioTime'),
  createEngagementCsvColumn('Action Posts EN Audios', 'taskArticleEnAudioTime'),
  createEngagementCsvColumn('Action Posts ES Audios', 'taskArticleEsAudioTime'),
  createEngagementCsvColumn('Queries EN Audios', 'queryEnAudioTime'),
  createEngagementCsvColumn('Queries ES Audios', 'queryEsAudioTime'),
]

const csvFieldsSession: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last Name', value: 'userLastName' },
  { label: 'Login', value: 'userEmail' },
  { label: 'Access Code', value: 'userAccessCode' },
  createCohortsCsvColumn('userCohorts'),
  {
    label: 'Login Date',
    value: (data: EngagementPerSession) => formatDate(data.startedAt, 'YYYY-MM-DD HH:mm:ss'),
  },
  createEngagementCsvColumn('Total Engagement Time', 'totalTime'),
]

const csvFieldsSearches: CSVField[] = [
  { label: 'Anonymous ID', value: 'anonymousId' },
  { label: 'First Name', value: 'firstName' },
  { label: 'Last Name', value: 'lastName' },
  { label: 'Login', value: 'email' },
  { label: 'Access Code', value: 'userAccessCode' },
  createCohortsCsvColumn('cohorts'),
  {
    label: 'Login Date',
    value: (data: EngagementPerUserSearches) => formatDate(data.loginDate, 'YYYY-MM-DD HH:mm:ss'),
  },
  { label: 'Terms or Phrase', value: 'text' },
  { label: 'Type', value: 'type' },
  { label: 'Number of Searches', value: 'countOfSearches' },
  { label: 'Found Content', value: 'countOfResults' },
]

export const csvFieldsCertificates: CSVField[] = [
  { label: 'Anonymous ID', value: 'anonymousId' },
  { label: 'First Name', value: 'firstname' },
  { label: 'Last Name', value: 'lastname' },
  { label: 'Login', value: 'email' },
  { label: 'ORG/State/UNIT', value: formatOrgStateUnit },
  { label: 'Access Code', value: 'userAccessCode' },
  createCohortsCsvColumn('cohorts'),
  { label: 'Certificate Name', value: 'name' },
  {
    label: 'Date',
    value: (data: EngagementPerUserCertificate) =>
      formatDate(data.createdAt, 'YYYY-MM-DD HH:mm:ss'),
  },
  {
    label: 'Certificate Type',
    value: (data: EngagementPerUserCertificate) => {
      return CertificateRuleTypeLabel[data.type] || PLACEHOLDER
    },
  },
  { label: 'Time Spend (h)', value: 'timeSpent' },
]

export const csvFieldsSharing: CSVField[] = [
  { label: 'Anonymous ID', value: 'userAnonymousId' },
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last Name', value: 'userLastName' },
  { label: 'Login', value: 'userEmail' },
  { label: 'Access Code', value: 'userAccessCode' },
  createCohortsCsvColumn('userCohorts'),
  {
    label: 'Shared Date',
    value: (data: SharedPerUserReport) => formatDate(data.sharedAt, 'YYYY-MM-DD HH:mm:ss'),
  },
  {
    label: 'Content Type',
    value: (data: SharedPerUserReport) => getSharingContentType(data),
  },
  { label: 'Title', value: 'contentName' },
  {
    label: 'Shared With',
    value: (data: SharedPerUserReport) => {
      const { receiverEmail, receiverFirstName, receiverLastName } = data
      const fullName = `${receiverFirstName} ${receiverLastName}`
      return `${receiverEmail} / ${fullName}`
    },
  },
]

type IReportsActions = {
  [key in EngagementReportTypes]: Nullish<RowAction[]>
}

type IReportsTabOptions = {
  [key in EngagementReportTypes]: {
    columns: ColDef[]
    title: string
    csvName: string
    csvFields: CSVField[]
    hasDateFilter: boolean
    hasUserTypeFilter: boolean
  }
}

const ReportsTabOptions: IReportsTabOptions = {
  [EngagementReportTypes.Course]: {
    title: 'Course Engagement per User',
    columns: activeColumnDefs,
    csvName: 'Course Engagement per User Report.csv',
    csvFields: csvFieldsActive,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.Lesson]: {
    title: 'Lesson Engagement per User',
    columns: lessonColumnDefs,
    csvName: 'Lesson Engagement per User Report.csv',
    csvFields: csvFieldsLesson,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.LearnLibrary]: {
    title: 'Learn Library Engagement per User',
    columns: learnArticlesColumnDefs,
    csvName: 'Learn Library Engagement Report.csv',
    csvFields: csvFieldsLearnArticles,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.CareArticle]: {
    title: 'Timeline Posts Engagement per User',
    columns: careArticlesColumnDefs,
    csvName: 'Timeline Posts Engagement Report.csv',
    csvFields: csvFieldsCareArticles,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.Query]: {
    title: 'Query Engagement Per User',
    columns: queryColumnDefs,
    csvName: 'Query Engagement Report.csv',
    csvFields: csvFieldsQuery,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.Total]: {
    title: 'Total Engagement Per User',
    columns: totalColumnDefs,
    csvName: 'User Total Engagement Report.csv',
    csvFields: csvFieldsTotal,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.Session]: {
    title: 'Login Report',
    columns: sessionColumnDefs,
    csvName: 'Login Report.csv',
    csvFields: csvFieldsSession,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.Searches]: {
    title: 'Searches per User',
    columns: searchesColumnDefs,
    csvName: 'Searches per User.csv',
    csvFields: csvFieldsSearches,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
  [EngagementReportTypes.Certificates]: {
    title: 'Certificates per User',
    columns: certificatesColumnDefs,
    csvName: 'Certificates per User.csv',
    csvFields: csvFieldsCertificates,
    hasDateFilter: true,
    hasUserTypeFilter: false,
  },
  [EngagementReportTypes.Sharing]: {
    title: 'Shares per User',
    columns: sharingColumnDefs,
    csvName: 'Shares per User.csv',
    csvFields: csvFieldsSharing,
    hasDateFilter: true,
    hasUserTypeFilter: true,
  },
}

const analystForbiddenFields = ['userFirstName', 'userLastName', 'userEmail']

const ReportOrder = [
  EngagementReportTypes.Course,
  EngagementReportTypes.Lesson,
  EngagementReportTypes.LearnLibrary,
  EngagementReportTypes.CareArticle,
  EngagementReportTypes.Query,
  EngagementReportTypes.Total,
  EngagementReportTypes.Session,
  EngagementReportTypes.Searches,
  EngagementReportTypes.Certificates,
  EngagementReportTypes.Sharing,
]

export const ReportsEngagementUser = () => {
  const navigate = useNavigate()
  const { engagementType } = useParams()
  const { isAnalist } = useAuthContext()

  const selectedTab = useMemo(() => {
    if (!engagementType) return EngagementReportTypes.Course

    return engagementType as EngagementReportTypes
  }, [engagementType])

  const tabOptions = useMemo(() => ReportsTabOptions[selectedTab], [selectedTab])

  const handleChangeSelectedTab = useCallback(
    (_: React.SyntheticEvent, newValue: EngagementReportTypes) => {
      const route = getRoute(routes.reports_engagement_user, { engagementType: newValue })
      navigate(route)
    },
    [navigate],
  )

  const [cohortsFilterValue, setCohortsFilterValue] = useState<Option[]>([])

  const handleDetailsClick = useCallback(
    (sessionUuid: number) => {
      const route = getRoute(routes.reports_engagement_user_session, { sessionUuid })
      navigate(route)
    },
    [navigate],
  )

  const actions: IReportsActions = useMemo(() => {
    return {
      [EngagementReportTypes.Course]: null,
      [EngagementReportTypes.Lesson]: null,
      [EngagementReportTypes.LearnLibrary]: null,
      [EngagementReportTypes.CareArticle]: null,
      [EngagementReportTypes.Query]: null,
      [EngagementReportTypes.Total]: null,
      [EngagementReportTypes.Session]: [{ name: 'See details', onClick: handleDetailsClick }],
      [EngagementReportTypes.Searches]: null,
      [EngagementReportTypes.Certificates]: null,
      [EngagementReportTypes.Sharing]: null,
    }
  }, [handleDetailsClick])

  const columns = useMemo(() => {
    const tabActions = actions[selectedTab]
    let columns = tabOptions.columns

    if (isAnalist && selectedTab !== EngagementReportTypes.Session) {
      columns = columns.filter((item) => !analystForbiddenFields.includes(item.field || ''))
    }

    if (!tabActions) {
      return columns
    }

    return [...columns, getActionColumn(tabActions, 'sessionUuid')]
  }, [actions, isAnalist, selectedTab, tabOptions.columns])

  const {
    rangeType,
    year,
    month,
    selectedDate,
    dateInterval,
    onChangeDateInterval,
    onChangeRange,
    onChangeYear,
    onChangeMonth,
    setSelectedDate,
  } = useDateRange()

  const { sharedContentTypeFilter, sharedContentTypeFilterValue } = useSharedContentTypeFilter()
  const { UserTypeFilter, userTypeFilterValue } = useUserTypeFilter()

  const getActiveEngagementWrapper = useCallback(
    (req: BaseTableRequest) => {
      let filter: FilterModel = {
        ...(req.filter || {}),
      }

      if (cohortsFilterValue.length > 0) {
        filter.cohortId = {
          type: 'number',
          op: 'in',
          value: cohortsFilterValue.map((cohort) => cohort.value),
        } as FilterModelNumberArray
      }

      if (tabOptions.hasUserTypeFilter && userTypeFilterValue) {
        filter = { ...filter, ...userTypeFilterValue }
      }

      if (selectedTab === EngagementReportTypes.Sharing) {
        if (filter.contentName) {
          filter = applyContentNameFilter(filter)
          delete filter.contentName
        }

        if (filter.receiverEmail) {
          const filterValue: FilterModelText = {
            type: 'text',
            op: EOp.contains,
            value: (filter.receiverEmail as FilterModelText).value,
          }

          filter = {
            ...filter,
            or: [
              {
                receiverEmail: filterValue,
              },
              {
                receiverFirstName: filterValue,
              },
              {
                receiverLastName: filterValue,
              },
            ],
          }
          delete filter.receiverEmail
        }

        if (sharedContentTypeFilterValue) {
          filter = { ...filter, ...sharedContentTypeFilterValue }
        }
      }

      const request = { ...req, filter }

      const dateParams = prepareDateRangeParams(rangeType, year, month, selectedDate, dateInterval)

      if (selectedTab === EngagementReportTypes.Course) {
        return getCourseEngagementPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.Lesson) {
        return getLessonEngagementPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.LearnLibrary) {
        return getLearnLibraryEngagementPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.CareArticle) {
        return getCareArticlesEngagementPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.Query) {
        return getQueryEngagementPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.Total) {
        return getTotalEngagementPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.Session) {
        return getEngagementPerSessionReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.Searches) {
        return getSearchesPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.Certificates) {
        return getCertificatesPerUserReport({ ...request, params: dateParams })
      }

      if (selectedTab === EngagementReportTypes.Sharing) {
        return getSharePerUserReport({ ...request, params: dateParams })
      }

      return getTotalEngagementPerUserReport({ ...request, params: dateParams })
    },
    [
      sharedContentTypeFilterValue,
      cohortsFilterValue,
      userTypeFilterValue,
      tabOptions.hasUserTypeFilter,
      dateInterval,
      month,
      rangeType,
      selectedDate,
      selectedTab,
      year,
    ],
  )

  const getDataSourceActive = useGetDataSource(getActiveEngagementWrapper)
  const { onGridReady, gridApi, columnApi } = useGridControl(getDataSourceActive)

  const { csvLoading, onExportCSV } = useExportCSV(
    getActiveEngagementWrapper,
    gridApi,
    columnApi,
    tabOptions.csvFields,
    tabOptions.csvName,
  )

  useRefreshTable({ params: { rangeType, year, month, selectedDate }, gridApi })

  const handleChangeCohortFilter = useCallback(
    (newValue: Option[]) => {
      setCohortsFilterValue(newValue)
      // Infinite row model refresh
      gridApi?.purgeInfiniteCache?.()
    },
    [gridApi],
  )

  return (
    <>
      <TableToolbar
        left={
          <Tabs
            value={engagementType}
            onChange={handleChangeSelectedTab}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons
            sx={{ ml: '-24px', flex: 1 }}
          >
            {ReportOrder.map((type) => (
              <Tab key={type} value={type} label={getTitle(ReportsTabOptions[type].title)} />
            ))}
          </Tabs>
        }
        exportCSVEnable
        csvLoading={csvLoading}
        onExportCSV={onExportCSV}
      />

      <Stack flexDirection="row" mt={1} mb={2} className="gap-16">
        {tabOptions.hasDateFilter && (
          <DateRangeToolbar
            rangeType={rangeType}
            year={year}
            month={month}
            selectedDate={selectedDate}
            dateInterval={dateInterval}
            onChangeDateInterval={onChangeDateInterval}
            onChangeRange={onChangeRange}
            onChangeYear={onChangeYear}
            onChangeMonth={onChangeMonth}
            setSelectedDate={setSelectedDate}
          />
        )}

        {selectedTab !== EngagementReportTypes.Sharing && (
          <Box minWidth="200px">
            <UIFilterAsyncAutocomplete
              label="User Tags"
              value={cohortsFilterValue}
              onChange={handleChangeCohortFilter}
              queryId="cohorts"
              apiRequest={getCohorts}
            />
          </Box>
        )}

        {tabOptions.hasUserTypeFilter && <Box minWidth="250px">{UserTypeFilter}</Box>}

        {selectedTab === EngagementReportTypes.Sharing && (
          <Box minWidth="250px">{sharedContentTypeFilter}</Box>
        )}
      </Stack>

      <InfinityAgGrid columnDefs={columns} pagination onGridReady={onGridReady} />
    </>
  )
}
